import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { getFirebase } from './firebase';

// Auth state for the studio gate. `authorized` means the signed-in account is
// on the designers allowlist (a designers/{email} doc exists). A signed-in but
// non-allowlisted user is rejected at the gate.
export type AuthState = {
  loading: boolean;
  user: User | null;
  authorized: boolean;
  // True when the designer doc has role: 'admin'. Gates destructive actions.
  isAdmin: boolean;
};

export type AuthApi = AuthState & {
  signIn: () => void;
  signOutNow: () => void;
};

export function useAuth(): AuthApi {
  const [state, setState] = useState<AuthState>({
    loading: true,
    user: null,
    authorized: false,
    isAdmin: false,
  });

  useEffect(() => {
    const { auth, db } = getFirebase();
    return onAuthStateChanged(auth, async (user) => {
      if (!user?.email) {
        setState({ loading: false, user, authorized: false, isAdmin: false });
        return;
      }
      let authorized = false;
      let isAdmin = false;
      try {
        const snap = await getDoc(doc(db, 'designers', user.email));
        authorized = snap.exists();
        isAdmin = snap.data()?.role === 'admin';
      } catch (err) {
        console.warn('[studio] allowlist check failed', err);
      }
      setState({ loading: false, user, authorized, isAdmin });
    });
  }, []);

  const signIn = (): void => {
    const { auth } = getFirebase();
    signInWithPopup(auth, new GoogleAuthProvider()).catch((err) =>
      console.warn('[studio] sign-in failed', err),
    );
  };

  const signOutNow = (): void => {
    const { auth } = getFirebase();
    signOut(auth).catch(() => {});
  };

  return { ...state, signIn, signOutNow };
}
